import type { CustomCellRendererProps } from "ag-grid-react";
import { type JSX, memo, type PointerEvent, useMemo, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../tooltip/Tooltip";
import { parseSeries } from "../utils/sparklineCell";
import {
  baseline,
  canvas,
  marker,
  plot,
  wrapper
} from "./SvgSparklineCell.css";

export interface SvgSparklineCellParams {
  /**
   * A line for a curve, scaled to its own row's min and max; bars for a
   * nonnegative distribution, scaled from zero to the row's max (a negative
   * value draws as zero). Equal heights in two rows don't mean equal values.
   */
  kind: "line" | "bar";
  /** The stroke (line) or fill (bar) — a CSS colour, theme tokens included. */
  color: string;
  /** The hovered point's tooltip text; without it the cell has no tooltip. */
  label?: (index: number, value: number) => string;
}

// The viewBox stretches to the cell (`preserveAspectRatio="none"`), so these
// are plot units, not pixels — and, at 100, percentages of the plot box.
const WIDTH = 100;
const HEIGHT = 100;

interface Point {
  x: number;
  y: number;
}

interface Geometry {
  /** The series, a missing or non-numeric entry as NaN at its position. */
  series: number[];
  /** Each point's plot position; null where the series has no value. */
  points: (Point | null)[];
  /** The accessible name: what the shape shows, in words. */
  summary: string;
}

function geometryOf(value: unknown, kind: "line" | "bar"): Geometry | null {
  const series = parseSeries(value)?.map((point) =>
    typeof point === "number" && Number.isFinite(point) ? point : NaN
  );
  const finite = series?.filter(Number.isFinite) ?? [];
  if (!series || finite.length === 0) return null;

  const min = Math.min(...finite);
  const max = Math.max(...finite);
  let points: (Point | null)[];
  if (kind === "line") {
    const span = max - min;
    const step = series.length > 1 ? WIDTH / (series.length - 1) : 0;
    points = series.map((point, index) =>
      Number.isFinite(point)
        ? {
            x: series.length > 1 ? index * step : WIDTH / 2,
            // A flat series has no scale to stretch; it runs mid-height.
            y: span ? HEIGHT - ((point - min) / span) * HEIGHT : HEIGHT / 2
          }
        : null
    );
  } else {
    const top = Math.max(max, 0) || 1;
    const band = WIDTH / series.length;
    points = series.map((point, index) =>
      Number.isFinite(point)
        ? {
            x: index * band + band / 2,
            y: HEIGHT - (Math.max(point, 0) / top) * HEIGHT
          }
        : null
    );
  }

  const first = finite[0];
  const last = finite[finite.length - 1];
  const summary =
    `${finite.length} of ${series.length} points, ` +
    `from ${formatValue(min)} to ${formatValue(max)}, ` +
    `first ${formatValue(first)}, last ${formatValue(last)}`;
  return { series, points, summary };
}

function formatValue(value: number): string {
  return String(Number(value.toPrecision(3)));
}

function linePath(points: (Point | null)[]): string {
  let path = "";
  points.forEach((point, index) => {
    if (!point) return;
    const at = `${point.x.toFixed(2)},${point.y.toFixed(2)}`;
    const joined = index > 0 && points[index - 1] != null;
    const isolated = !joined && points[index + 1] == null;
    // A zero-length segment: the round cap draws an isolated point as a dot.
    path += joined ? `L${at}` : isolated ? `M${at}L${at}` : `M${at}`;
  });
  return path;
}

interface Hover {
  index: number;
  /** The cell value the index was taken from; a new value voids it. */
  source: unknown;
}

/**
 * A number series drawn as plain SVG — the light stand-in for
 * `agSparklineCellRenderer`, which builds a chart instance per cell. The
 * tooltip mounts only while the pointer is over the plot, so a resting cell
 * stays plain SVG. A series with no finite value draws nothing;
 * `sparklineSelector` withholds only an empty series.
 */
export const SvgSparklineCell = memo(function SvgSparklineCell({
  value,
  kind,
  color,
  label
}: CustomCellRendererProps & SvgSparklineCellParams): JSX.Element | null {
  const [hover, setHover] = useState<Hover | null>(null);
  const geometry = useMemo(() => geometryOf(value, kind), [value, kind]);
  if (!geometry) return null;
  const { series, points, summary } = geometry;

  function handlePointerMove(event: PointerEvent<SVGSVGElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - box.left) / (box.width || 1);
    const last = series.length - 1;
    const index =
      kind === "line"
        ? Math.round(fraction * last)
        : Math.floor(fraction * series.length);
    const clamped = Math.min(Math.max(index, 0), last);
    setHover(
      Number.isFinite(series[clamped])
        ? { index: clamped, source: value }
        : null
    );
  }

  const hovered = hover != null && hover.source === value ? hover.index : null;
  const hoveredValue = hovered == null ? NaN : series[hovered];
  const hoveredPoint = hovered == null ? null : points[hovered];
  const band = WIDTH / series.length;

  return (
    <div className={wrapper}>
      <div className={plot}>
        <svg
          className={canvas}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={summary}
          onPointerMove={label ? handlePointerMove : undefined}
          onPointerLeave={label ? () => setHover(null) : undefined}
        >
          {kind === "line" ? (
            <path
              d={linePath(points)}
              fill="none"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              style={{ stroke: color }}
            />
          ) : (
            <>
              {points.map((point, index) =>
                point ? (
                  <rect
                    // A series has a fixed position per index.
                    key={index}
                    x={index * band + band * 0.15}
                    y={point.y}
                    width={band * 0.7}
                    height={HEIGHT - point.y}
                    style={{ fill: color }}
                  />
                ) : null
              )}
              <line
                className={baseline}
                x1={0}
                y1={HEIGHT}
                x2={WIDTH}
                y2={HEIGHT}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
        </svg>
        {label &&
        hovered != null &&
        hoveredPoint &&
        Number.isFinite(hoveredValue) ? (
          <Tooltip
            open
            onOpenChange={(open) => {
              if (!open) setHover(null);
            }}
          >
            <TooltipTrigger asChild>
              <span
                className={marker}
                style={{
                  left: `${hoveredPoint.x}%`,
                  top: `${hoveredPoint.y}%`,
                  background: color
                }}
              />
            </TooltipTrigger>
            <TooltipContent side="top">
              {label(hovered, hoveredValue)}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
});
